import React from 'react';
import { Group, Stack, Text, UnstyledButton } from '@mantine/core';
import {
  IconAdjustmentsHorizontal, IconChevronDown, IconChevronRight, IconStarFilled,
} from '@tabler/icons-react';
import { defaultThemeStyle, themeStylesOf } from '../../../shared/theme/themeStyles';
import { useThemeConfiguration } from '../../context/ThemeConfigurationContext';
import { detachThemeStyle, linkThemeStyle, themeStyleValues } from '../themedBlocks';
import { useSelectedItemProps } from './useSelectedItemProps';
import { useInspectorSectionState } from './useInspectorSectionState';
import StyleCardPreview, { StyleCardPlaceholder } from '../../theme/StyleCardPreview';

/** Titre d'une section de la galerie, qui la replie (choix retenu, cf. useInspectorSectionState). */
function GallerySection({ title, collapsedByDefault = false, children }) {
  const [collapsed, toggle] = useInspectorSectionState(title, collapsedByDefault);
  const Chevron = collapsed ? IconChevronRight : IconChevronDown;
  return (
    <>
      <UnstyledButton onClick={toggle} aria-expanded={!collapsed}>
        <Group gap={4}>
          <Chevron size={12} aria-hidden />
          <Text size="xs" fw={600} c="dimmed">{title}</Text>
        </Group>
      </UnstyledButton>
      {!collapsed && children}
    </>
  );
}

const LINKED_TITLE = 'Ne plus suivre le style : ce bloc garde son aspect actuel, '
  + 'mais ne change plus avec le style, et tous ses réglages deviennent modifiables';
const CUSTOM_TITLE = 'Ce bloc ne suit aucun style : tous ses réglages sont modifiables';

/**
 * Tuile « Personnalisé ». Allumée quand le bloc ne suit aucun style ; tant
 * qu'il en suit un, `onDetach` en fait l'action qui l'en détache — le seul
 * moyen de régler à la main ce que le style impose.
 */
export function CustomStyleTile({ classes, isCustom, onDetach }) {
  const disabled = !isCustom && !onDetach;
  return (
    <button
      type="button"
      role="option"
      aria-selected={isCustom}
      title={onDetach ? LINKED_TITLE : CUSTOM_TITLE}
      disabled={disabled}
      className={`${classes.card} ${isCustom ? classes.cardSelected : ''} ${disabled ? classes.cardDisabled : ''}`}
      onClick={onDetach}
    >
      <StyleCardPlaceholder><IconAdjustmentsHorizontal size={20} /></StyleCardPlaceholder>
      <Text size="10px" c={isCustom ? 'editor.7' : 'dimmed'} lh={1.2} ta="center">
        Personnalisé
      </Text>
    </button>
  );
}

/**
 * Galerie de styles d'un bloc thémable. Section « Thème du site » : les
 * styles du thème, que le bloc SUIT (lien vivant, cf. puck/themedBlocks),
 * puis la tuile « Personnalisé » — qui l'en détache tant qu'il en suit un,
 * témoin une fois détaché. Section « Génériques »
 * (`children`, repliée par défaut) : les styles que le bloc recopie.
 *
 * @param {{
 *   kind: string, classes: object, renderPreview?: (values) => React.ReactNode,
 *   isCustom?: boolean, children?: React.ReactNode,
 * }} props
 *        `classes` : la grille et les tuiles de la galerie hôte, pour que les
 *        deux sections se ressemblent ; `renderPreview` reçoit les valeurs
 *        que le style pose sur le bloc (par défaut : le vrai bloc, réduit) ;
 *        `isCustom` : le bloc ne suit aucun style, générique compris (par
 *        défaut : aucun style du thème).
 */
export default function ThemeStyleGallery({
  kind, classes, isCustom: isCustomProp, children,
  renderPreview = (values) => <StyleCardPreview kind={kind} values={values} />,
}) {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const configuration = useThemeConfiguration();
  const styles = themeStylesOf(configuration, kind);
  const defaultId = defaultThemeStyle(configuration, kind)?.id;
  const linked = itemProps.themeStyleId != null;
  const isCustom = isCustomProp ?? !linked;

  return (
    <Stack gap={6}>
      <GallerySection title="Thème du site">
        <div className={classes.grid} role="listbox" aria-label="Styles du thème">
          {styles.map((style) => {
            const selected = linked && String(itemProps.themeStyleId) === String(style.id);
            return (
              <button
                key={style.id}
                type="button"
                role="option"
                aria-selected={selected}
                title={style.name}
                className={`${classes.card} ${selected ? classes.cardSelected : ''}`}
                onClick={() => updateItemProps(linkThemeStyle(kind, itemProps, style, configuration))}
              >
                {renderPreview(themeStyleValues(kind, style, configuration))}
                <Text size="10px" c={selected ? 'editor.7' : 'dimmed'} lh={1.2} ta="center" truncate>
                  {style.id === defaultId && (
                    <IconStarFilled size={8} aria-label="Style par défaut" style={{ marginRight: 2 }} />
                  )}
                  {style.name}
                </Text>
              </button>
            );
          })}
          <CustomStyleTile
            classes={classes}
            isCustom={isCustom}
            onDetach={linked ? () => updateItemProps(detachThemeStyle(kind, itemProps, configuration)) : undefined}
          />
        </div>
      </GallerySection>
      {children && (
        <GallerySection title="Génériques" collapsedByDefault>
          {children}
        </GallerySection>
      )}
    </Stack>
  );
}
